import { useCallback, useMemo, type ReactNode } from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';
import type { SortDirection, TableColumn, TableProps } from './types.js';

/** Fixed-column data grid. Not virtualized — suitable for ~500 rows and
 *  below. For larger datasets, pair with a virtualizer hook once that
 *  package lands.
 *
 *  Sorting:
 *    - Each sortable column must define `compare(a, b)`.
 *    - Click cycles `asc → desc → off` on that column, resetting others.
 *    - Controlled sort: pass `sort` + `onSortChange`.
 *
 *  Selection:
 *    - Opt-in via `selectable`. Renders a leading checkbox column with
 *      a header checkbox that toggles every row in view.
 *    - Controlled selection: pass `selected` + `onSelectionChange`.
 *
 *  Accessibility: follows the native table ARIA model — `role="table"`,
 *  `scope="col"` headers, `aria-sort` on the sorted column's header,
 *  `aria-selected` on selected rows. */
export function Table<Row>(props: TableProps<Row>): ReactNode {
  const {
    columns,
    rows,
    rowKey = (_row, i) => String(i),
    onRowClick,
    selected,
    onSelectionChange,
    defaultSort,
    sort,
    onSortChange,
    selectable = false,
    striped = true,
    stickyHeader = false,
    density = 'comfortable',
    emptyState,
    className,
    'aria-label': ariaLabel,
  } = props;

  const [sortState, setSortState] = useControllableState<{
    id: string;
    direction: SortDirection;
  } | null>({
    defaultValue: defaultSort ?? null,
    ...(sort !== undefined ? { value: sort } : {}),
    ...(onSortChange !== undefined ? { onChange: onSortChange } : {}),
  });

  const [selectionState, setSelectionState] = useControllableState<ReadonlySet<string>>({
    defaultValue: new Set<string>(),
    ...(selected !== undefined ? { value: selected } : {}),
    ...(onSelectionChange !== undefined ? { onChange: onSelectionChange } : {}),
  });

  const keyedRows = useMemo(
    () => rows.map((row, i) => ({ row, key: rowKey(row, i), index: i })),
    [rows, rowKey],
  );

  const sortedRows = useMemo(() => {
    if (sortState === null) return keyedRows;
    const col = columns.find((c) => c.id === sortState.id);
    const compare = col?.compare;
    if (compare === undefined) return keyedRows;
    const copy = [...keyedRows];
    copy.sort((a, b) => compare(a.row, b.row));
    if (sortState.direction === 'desc') copy.reverse();
    return copy;
  }, [keyedRows, sortState, columns]);

  const onHeaderClick = useCallback(
    (col: TableColumn<Row>) => {
      if (col.sortable !== true) return;
      if (sortState === null || sortState.id !== col.id) {
        setSortState({ id: col.id, direction: 'asc' });
      } else if (sortState.direction === 'asc') {
        setSortState({ id: col.id, direction: 'desc' });
      } else {
        setSortState(null);
      }
    },
    [sortState, setSortState],
  );

  const toggleAll = useCallback(() => {
    const next = new Set<string>();
    if (selectionState.size < keyedRows.length) {
      for (const r of keyedRows) next.add(r.key);
    }
    setSelectionState(next);
  }, [selectionState, keyedRows, setSelectionState]);

  const toggleRow = useCallback(
    (key: string) => {
      const next = new Set(selectionState);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setSelectionState(next);
    },
    [selectionState, setSelectionState],
  );

  const allSelected =
    selectable && keyedRows.length > 0 && selectionState.size === keyedRows.length;
  const someSelected = selectable && selectionState.size > 0 && !allSelected;

  const classes = [
    'vq-table',
    `vq-table--${density}`,
    striped && 'vq-table--striped',
    stickyHeader && 'vq-table--sticky-header',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const columnLabels = columns.map((col) => tableHeaderLabel(col.header));

  /* A table with zero columns isn't renderable — colSpan would be 0 and
   * produce invalid DOM. Callers almost certainly made a mistake, but
   * we render the empty-state text instead of crashing so the symptom
   * is visible rather than mysterious. */
  if (columns.length === 0) {
    return (
      <div className="vq-table-wrap">
        <div className="vq-table__empty" style={{ padding: 'var(--vq-space-6)' }} role="status">
          {emptyState ?? 'No columns'}
        </div>
      </div>
    );
  }

  return (
    <div className="vq-table-wrap" data-component="Table">
      <table role="table" aria-label={ariaLabel} className={classes}>
        <thead className="vq-table__head">
          <tr>
            {selectable && (
              <th scope="col" className="vq-table__cell vq-table__cell--check">
                <input
                  type="checkbox"
                  className="vq-table__select"
                  checked={allSelected}
                  ref={(el) => {
                    if (el !== null) el.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {columns.map((col) => {
              const isSorted = sortState !== null && sortState.id === col.id;
              const ariaSort = isSorted
                ? sortState.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : col.sortable === true
                  ? 'none'
                  : undefined;
              const style: React.CSSProperties = {};
              if (col.width !== undefined) style.width = col.width;
              if (col.maxWidth !== undefined) style.maxWidth = col.maxWidth;
              return (
                <th
                  key={col.id}
                  scope="col"
                  className="vq-table__cell vq-table__cell--head"
                  data-align={col.align ?? 'start'}
                  aria-sort={ariaSort}
                  style={style}
                >
                  {col.sortable === true ? (
                    <button
                      type="button"
                      className="vq-table__sort"
                      onClick={() => {
                        onHeaderClick(col);
                      }}
                    >
                      <span className="vq-table__sort-label">{col.header}</span>
                      <span className="vq-table__sort-icon" aria-hidden="true">
                        {isSorted ? (sortState.direction === 'asc' ? '▲' : '▼') : '▵'}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="vq-table__body">
          {sortedRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="vq-table__empty">
                {emptyState ?? 'No data'}
              </td>
            </tr>
          ) : (
            sortedRows.map(({ row, key, index }) => {
              const isSel = selectionState.has(key);
              return (
                <tr
                  key={key}
                  className="vq-table__row"
                  data-selected={isSel ? 'true' : 'false'}
                  aria-selected={isSel}
                  onClick={
                    onRowClick !== undefined
                      ? () => {
                          onRowClick(row, index);
                        }
                      : undefined
                  }
                >
                  {selectable && (
                    <td className="vq-table__cell vq-table__cell--check">
                      <input
                        type="checkbox"
                        className="vq-table__select"
                        checked={isSel}
                        onChange={() => {
                          toggleRow(key);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        aria-label={`Select row ${String(index + 1)}`}
                      />
                    </td>
                  )}
                  {columns.map((col, columnIndex) => {
                    const style: React.CSSProperties = {};
                    if (col.maxWidth !== undefined) {
                      style.maxWidth = col.maxWidth;
                    }
                    return (
                      <td
                        key={col.id}
                        className="vq-table__cell"
                        data-align={col.align ?? 'start'}
                        data-label={columnLabels[columnIndex]}
                        style={style}
                      >
                        {col.accessor(row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function tableHeaderLabel(header: ReactNode): string | undefined {
  if (typeof header === 'string' || typeof header === 'number') return String(header);
  if (Array.isArray(header)) {
    const parts = header
      .map((part) => tableHeaderLabel(part))
      .filter((part): part is string => part !== undefined && part.length > 0);
    return parts.length > 0 ? parts.join(' ') : undefined;
  }
  return undefined;
}
